import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { ListJobPositionItem } from '@restworld/data-services';
import { EmployerJobListingCard } from '@restworld/ui-ds';
import { useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

interface EmployerJobListingCardsProps {
  jobPositionItems?: ListJobPositionItem[];
}

function EmployerJobListingCardsList({ jobPositionItems }: EmployerJobListingCardsProps) {
  const jobListingCards = useMemo(
    () =>
      !!jobPositionItems?.length && (
        <>
          {jobPositionItems.map((jp) => (
            <JobPositionItem key={jp.job_position.id} {...jp} />
          ))}
        </>
      ),
    [jobPositionItems]
  );

  return <>{jobListingCards}</>;
}

export default EmployerJobListingCardsList;

function JobPositionItem(jobPositionItem: ListJobPositionItem) {
  const { id, jobTitle, occupation, staffRequested, status, labels, currentRestaurant, candidates } =
    useJobPositionItem(jobPositionItem);
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();

  const handleClick = useCallback(() => {
    trackEvent('employer_view_jp_details', {
      email: user?.email,
      jp_id: id,
      jp_title: jobTitle,
      jp_status: status?.label,
      restaurant_name: currentRestaurant?.name,
    });
  }, [currentRestaurant?.name, id, jobTitle, status, trackEvent, user?.email]);

  return (
    <EmployerJobListingCard
      key={id}
      href={`/employer/job-positions/${id}`}
      jobTitle={jobTitle}
      restaurant={currentRestaurant}
      labels={labels}
      occupationLabel={occupation?.label_it.label}
      staffRequested={staffRequested}
      status={status}
      candidates={candidates}
      domainInterestKey={occupation?.domain_interest_var?.key}
      onClick={handleClick}
    />
  );
}

function useJobPositionItem({
  job_position,
  number_of_shortlisted_workers,
  all_time_number_of_interested_workers,
  number_of_visits,
  number_of_workers_not_viewed_by_employer,
  applicants_avatar,
}: ListJobPositionItem) {
  const { t: translateCommon } = useTranslation('common');

  const { id, job_title, restaurant, occupation, staff_requested, status } = job_position || {};

  const labels = useMemo(
    () => ({
      visits: number_of_visits,
      candidates: all_time_number_of_interested_workers,
      qualified: number_of_shortlisted_workers,
    }),
    [all_time_number_of_interested_workers, number_of_shortlisted_workers, number_of_visits]
  );

  const currentRestaurant = useMemo(
    () => ({
      address: restaurant?.address,
      logo: restaurant?.restaurant_logo,
      name: restaurant?.restaurant_name,
    }),
    [restaurant?.address, restaurant?.restaurant_logo, restaurant?.restaurant_name]
  );

  const applicants = useMemo(() => applicants_avatar || [], [applicants_avatar]);

  const employerCandidates = useMemo(
    () =>
      applicants.map((candidate) => ({
        firstName: candidate.name,
        lastName: candidate.surname,
        image: candidate.avatar_url,
      })),
    [applicants]
  );

  let candidatesDisplayMessage: string;
  if (number_of_workers_not_viewed_by_employer === 1)
    candidatesDisplayMessage = translateCommon('new_candidate_to_view');
  else candidatesDisplayMessage = translateCommon('candidates_to_view');

  const candidates = useMemo(
    () => ({
      count: number_of_workers_not_viewed_by_employer,
      candidates: employerCandidates,
      message: candidatesDisplayMessage,
    }),
    [candidatesDisplayMessage, employerCandidates, number_of_workers_not_viewed_by_employer]
  );

  return {
    id,
    jobTitle: job_title,
    occupation,
    staffRequested: staff_requested,
    status,
    labels,
    currentRestaurant,
    candidates,
  };
}
